import { LanguageContext } from "../../../../context/LanguageContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import {
  AmountBox,
  AppAmountBoxWrapper,
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../../common";
import AppTableFilters from "../../../common/AppTableFilters";
import { useNavigate, useLocation } from "react-router-dom";
import Transactions from "./Transactions";
import { UBButton } from "../../../../Libraries";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { initialFilters } from "./FilterModal";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { handleApi } from "../../../../apis";
import {
  GET_TRANSACTION_LIST_V2,
  GETWALLETTRANSACTION,
} from "../../../../apis/configuration";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import axios from "axios";
import { buildFiltersConfig, getStartAndEndDate } from "../../../../utils";
import moment from "moment";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import {
  DateFilter,
  EMIONBOARDINGSTATUS,
  TABLE_FILTER_TXNS,
} from "../../../../constants";
import walletMainStyles from "../WalletMain.module.css";
import allTransactionStyles from "./AllTransaction.module.css";
import { useWalletContext } from "../../../../context/WalletContext";

const WALLET_TXN_TYPE_OPTIONS = [
  { label: "Recharge", value: "recharge" },
  { label: "Bill payments", value: "bill_payment" },
  { label: "Vouchers", value: "voucher_payment" },
];

const AllTransaction = () => {
  const [I18n] = useContext(LanguageContext);
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const { walletData } = useWalletContext()

  const [searchQ, setSearchQ] = useState("");
  const [isResetFilter, setIsResetFilter] = useState(false);
  const debounceSearchName = useDebouncedValue(searchQ, 500);
  const [filters, setFilters] = useState(initialFilters);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitalLoading, setIsInitalLoading] = useState(true);
  const [hasNext, setHasNext] = useState(false);
  const [nextId, setNextId] = useState("");
  const [moneyIn, setMoneyIn] = useState(0);
  const [moneyOut, setMoneyOut] = useState(0);
  const navigate = useNavigate();
  let cancelToken = useRef();
  const filtersRef = useRef(filters); // Create a ref to store the latest filters

  useEffect(() => {
    triggerLogEvent(analytics, "w_transactions_all");
  }, []);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    return () => {
      if (cancelToken.current) {
        cancelToken.current.cancel(
          "AllTransaction unmounted, canceling pending request.",
        );
      }
    };
  }, []);

  const isAppliedFilter = useMemo(() => {
    const vt = filters.viewTransaction || "";
    const viewTxApplied = Boolean(vt);
    const dateApplied =
      filters.dateType !== "All" &&
      Boolean(filters.startDate && filters.endDate);
    const amountApplied = Boolean(filters.fromAmount || filters.toAmount);
    const searchApplied = Boolean((debounceSearchName || "").trim());
    return viewTxApplied || dateApplied || amountApplied || searchApplied;
  }, [filters, debounceSearchName]);

  const keywordsPlaceholder = `${I18n.search_by} ${I18n.transaction_id.toLowerCase()}`;

  const appTableFilterState = useMemo(
    () => ({
      keywords: searchQ,
      view: filters.viewTransaction || "",
      date: {
        startDate: filters.startDate ?? null,
        endDate: filters.endDate ?? null,
      },
      amount: {
        min:
          filters.fromAmount != null && filters.fromAmount !== ""
            ? String(filters.fromAmount)
            : "",
        max:
          filters.toAmount != null && filters.toAmount !== ""
            ? String(filters.toAmount)
            : "",
      },
    }),
    [filters, searchQ],
  );

  const customView = [
    { id: "deposited", label: I18n.money_in, value: "deposited" },
    { id: "withdraw", label: I18n.money_out, value: "withdraw" },
    { id: "refund", label: I18n.refund, value: "refund" },
    { id: "recharge", label: I18n.recharge, value: "recharge" },
    { id: "bill_payment", label: I18n.bill_payments, value: "bill_payment" },
    { id: "voucher_payment", label: I18n.vouchers, value: "voucher_payment" },
    { id: 'sms_bundle_payment', label: I18n.sms_bundles, value: 'sms_bundle_payment' },
    { id: 'shop', label: I18n.shop, value: 'shop' },
  ];

  const appliedChips = useMemo(() => {
    const chips = [];
    if ((searchQ || "").trim()) {
      chips.push({
        id: "keywords",
        label: "Search",
        value: searchQ.trim(),
        count: 1,
      });
    }
    const typeVals = filters.viewTransaction || "";
    if (typeVals) {
      chips.push({
        id: "view",
        label: "View",
        value:
          customView.find((option) => option.value === typeVals[0])?.label ??
          typeVals[0],
      });
    }
    if (filters.startDate || filters.endDate) {
      chips.push({
        id: "date",
        label: "Date",
        value: `${filters.startDate ? moment(filters.startDate).format("MMM DD") : "Start"} - ${filters.endDate ? moment(filters.endDate).format("MMM DD") : "End"
          }`,
      });
    }
    if (filters.fromAmount || filters.toAmount) {
      chips.push({
        id: "amount",
        label: "Amount",
        value: `Amount: "Rs. ${formatNum(filters.fromAmount) || 0} - ${formatNum(filters.toAmount) || "∞"}"`,
      });
    }
    return chips;
  }, [filters, searchQ]);

  useEffect(() => {
    setIsInitalLoading(true);
    cancelPreviousCall();
    setTransactions([]);
    setPage(1);
    setHasNext(false);
    setNextId("");

    if (
      walletData &&
      walletData?.account_info?.emi_onboarding_status == EMIONBOARDINGSTATUS.VERIFIED
    ) {
      fetchTransactionList({
        page: 1,
        search: debounceSearchName,
        filter: filtersRef.current,
        nextId: "",
      });
    }
  }, [debounceSearchName, walletData]);

  const fetchTransactionList = async ({
    page,
    search,
    filter: filterObj,
    nextId: nextIdParam,
  }) => {
    setIsLoading(true);
    let transType = filterObj?.viewTransaction || "";
    try {
      let params = {
        page: page,
        limit: 20,
      };
      let filter = {};
      if (nextId)
        params["next_id"] =
          typeof nextIdParam === "string" ? nextIdParam : nextId;
      if (search) params["search"] = search;
      if (filterObj?.startDate)
        params["start_date"] = moment(filterObj?.startDate).format(
          "YYYY-MM-DD",
        );
      if (filterObj?.endDate)
        params["end_date"] = moment(filterObj?.endDate).format("YYYY-MM-DD");
      if (filterObj?.fromAmount || filterObj?.toAmount) {
        filter["amount"] = {};
        if (filterObj?.fromAmount != null)
          filter["amount"].min = filterObj?.fromAmount;
        if (filterObj?.toAmount != null)
          filter["amount"].max = filterObj?.toAmount;
      }
      if (transType.length) filter["txn_type"] = transType;
      params["filter"] = filter;
      const data = await handleApi(
        GET_TRANSACTION_LIST_V2,
        params,
        cancelToken.current,
        navigate,
      );
      if (data?.success) {
        setTransactions(
          page == 1
            ? [...data?.transactions]
            : [...transactions, ...data?.transactions],
        );

        if (page == 1) {
          let bill_payment = data?.bill_payments || 0;
          let recharges = data?.recharges || 0;
          let vouchers = data?.vouchers || 0;

          let recharge_commission = data?.recharge_commission || 0;
          let bill_commission = data?.bill_commission || 0;
          let voucher_commission = data?.voucher_commission || 0;

          let total = bill_payment + recharges + vouchers;
          let totalIn =
            recharge_commission + bill_commission + voucher_commission;

          const noResults = data.total_count === 0;
          let moneyOut = noResults
            ? 0
            : transType?.length == 0
              ? data?.money_out
              : total;
          let moneyIn = noResults
            ? 0
            : transType?.length == 0
              ? data?.money_in
              : totalIn;

          setMoneyIn(moneyIn || 0);
          setMoneyOut(moneyOut || 0);
        }

        setHasNext(data.has_next);
        setNextId(data.next_id);
        setIsLoading(false);
        setIsInitalLoading(false);
      } else {
        if (data?.transactions?.length == 0) {
          setMoneyIn(0);
          setMoneyOut(0);
        }
        setIsLoading(false);
        setIsInitalLoading(false);
      }
    } catch (error) {
      if (axios.isCancel(error)) return;
      showSnackbar({
        message: error?.response?.data?.detail || error?.response?.data?.message || "Something went wrong while fetching transactions",
        severity: "error",
        icon: "",
      });
      setIsLoading(false);
      setIsInitalLoading(false);
    }
  };

  const cancelPreviousCall = () => {
    if (cancelToken.current)
      cancelToken.current.cancel(
        "New request appears, canceling previous request.",
      );
    cancelToken.current = axios.CancelToken.source();
  };
  const handleSetFilter = (e) => {
    let startDate = null;
    let endDate = null;
    if (e.dateType === "Custom") {
      startDate = e?.startDate;
      endDate = e?.endDate;
      setFilters({ ...e });
    } else {
      const { startDate: start, endDate: end } = getStartAndEndDate(e.dateType);
      startDate = start;
      endDate = end;
      setFilters({ ...e, startDate, endDate });
    }
    setHasNext(false);
    setNextId("");
    cancelPreviousCall();
    setTransactions([]);
    setIsInitalLoading(true);
    setPage(1);
    fetchTransactionList({
      page: 1,
      search: debounceSearchName,
      filter: { ...e, startDate, endDate },
      nextId: "",
    });
  };

  const handleAppTableFilterChange = (key, value) => {
    triggerLogEvent(analytics, "w_transactions_filter_applied");
    const current = filtersRef.current;
    const next = { ...current };
    const k = String(key).toLowerCase();

    if (k === "view") {
      next.viewTransaction = [value];
    } else if (k === "keywords") {
      setSearchQ(value ?? "");
      return;
    } else if (k === "date") {
      if (value?.startDate || value?.endDate) {
        next.dateType = "Custom";
        next.startDate = value.startDate ?? null;
        next.endDate = value.endDate ?? null;
      } else {
        next.dateType = DateFilter[0];
        next.startDate = null;
        next.endDate = null;
      }
    } else if (k === "amount") {
      next.fromAmount =
        value?.min !== "" && value?.min != null ? Number(value.min) : null;
      next.toAmount =
        value?.max !== "" && value?.max != null ? Number(value.max) : null;
    }
    setHasNext(false);
    setNextId("");
    handleSetFilter(next);
  };

  const handleRemoveFilterChip = (chip) => {
    triggerLogEvent(analytics, "w_transactions_filter_removed");
    const current = filtersRef.current;
    switch (String(chip.label).toLowerCase()) {
      case "keywords":
      case "search":
        setSearchQ("");
        break;
      case "view":
        handleSetFilter({ ...current, viewTransaction: "" });
        break;
      case "date":
        handleSetFilter({
          ...current,
          dateType: DateFilter[0],
          startDate: null,
          endDate: null,
        });
        break;
      case "amount":
        handleSetFilter({ ...current, fromAmount: null, toAmount: null });
        break;
      default:
        break;
    }
  };
  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.transactions,
              isBack: true,
              onBack: () => navigate(-1),
              id: "all_transactions",
            }}
          ></TitleAndActionBox>
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppTableFilters
              filters={TABLE_FILTER_TXNS}
              filtersConfig={buildFiltersConfig(TABLE_FILTER_TXNS, null, null, null, null, null, null, customView, null, I18n)}
              filterState={appTableFilterState}
              onFilterChange={handleAppTableFilterChange}
              appliedChips={appliedChips}
              onRemoveChip={handleRemoveFilterChip}
              isResetFilter={isResetFilter}
              setIsResetFilter={setIsResetFilter}
            >
              <UBButton
                id="all_transactions_view_statement_btn"
                title={I18n.view_statement}
                className={allTransactionStyles.viewStatement}
                onClick={() => navigate("/Ucash/ViewBankStatement")}
              />
            </AppTableFilters>

            <Box className={allTransactionStyles.transactionViewAllListWrapper}>
              {isInitalLoading ? (
                <Box
                  className={`${walletMainStyles.transactionListLoadingBox} h-100`}
                >
                  <CircularProgress size={25} />
                </Box>
              ) : (
                <Transactions
                  list={transactions}
                  hasNext={hasNext}
                  setFilter={setFilters}
                  filters={filters}
                  initialFilters={initialFilters}
                  setPage={setPage}
                  fetchTransactionList={fetchTransactionList}
                  isLoading={isLoading}
                  debounceSearchName={debounceSearchName}
                  isAppliedFilter={isAppliedFilter}
                  search={debounceSearchName}
                  handleSetFilter={handleSetFilter}
                  onClearSearch={() => {
                    setSearchQ("");
                    setIsResetFilter(true);
                    setNextId("");
                    setHasNext(false);
                  }}
                />
              )}
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default AllTransaction;
